import { memo } from "react";
import { FormControl, Select, Skeleton, Typography } from "@mui/material";
import { faGripVertical, faTrashCan } from "@fortawesome/free-solid-svg-icons";

import { colorPrimary, colorRed } from "../../../constants/colors";
import { ConditionType, DepthType, OperationType } from "../../../types/ses/sesItemConfigTypes";
import { RobotActionEventType } from "../../../types/ses/sesRobotTypes";
import ButtonIcon from "../../../ui/ButtonIcon/ButtonIcon";
import { CustomMenuItem, CustomTypographyAsCheckbox } from "@/ui";
import IconWithPopupInfo from "../../IconWithPopupInfo/IconWithPopupInfo";
import { IConditionProps } from "./Condition.props";
import styles from "./Condition.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Hint } from "@/HOC";
import { CONDITIONS_LIST_ICONS, DEPTH_OPTIONS_ICONS, DEPTH_OPTIONS_LIST, OPERATION_CONDITIONS_ICONS, OPERATION_CONDITIONS_LIST } from "./Condition.const";
import { IconSelect, TextfieldWithAutocompleteSmall } from "@/ui";
import { ROBOT_EVENT_LIST } from "@/constants/robotConfigLists";
import { useConditionDragDrop } from "../useDragNDrop";
import { useCondition } from "./useCondition";

const Condition = ({
	conditionData,
	conditionBlockIdx,
	conditionIdx,
	isDisable,
	changeFlgHandler,
	conditionFor,
	showDragIcon,
	isConditionReady,
	setIsConditionReady,
	// isThisOrBlock = false,
	orBlockIndex,
	conditionWithOrOption = true,
	dragIdx,
	dataElemListOptions,
}: IConditionProps): JSX.Element => {
	const {
		// STATE
		extendedselectConditionType,
		selectDepth,
		selectOperation,
		selectEventId,
		confirmationValue,
		checkIntervalEquality,

		// SETTERS
		setSelectEventId,

		//  FORM
		formik,

		// HANDLERS
		changeCheckIntervalEquality,
		changeTypeConditionRunHandler,
		changeValuesConditionRunHandler,
		deleteConditionHandler,
		handleChangeSelectDepth,
		handleChangeSelectOperation,

		// OPTIONS
		dataElemOptions,
		timeIntervalsOptions,
		variableNamesOptions,
		variableValuesOptions,
		conditionsTypesList,

		// STORE DATA
		dataElemList,
		endpointElem,
		sesHints,

		// HELPERS
		translate,
		dispatch,
	} = useCondition({
		conditionData,
		conditionBlockIdx,
		conditionIdx,
		isDisable,
		changeFlgHandler,
		conditionFor,
		showDragIcon,
		isConditionReady,
		setIsConditionReady,
		orBlockIndex,
		conditionWithOrOption,
		dragIdx,
		dataElemListOptions,
	});

	const ref = useConditionDragDrop({
		conditionIdx,
		conditionBlockIdx,
		orBlockIndex, // для OR-блока
		dragIdx, // индекс внутри OR-блока, если есть
		conditionFor,
		changeFlgHandler,
		dispatch,
		endpointElem,
		conditionWithOrOption: true,
		conditionEntryIdx: "conditionEntryBlockIdx" in conditionFor ? conditionFor.conditionEntryBlockIdx : null,
	});

	return (
		<>
			{!isConditionReady && (
				<div className={styles.conditionSkeleton}>
					<Skeleton sx={{ borderRadius: "4px" }} animation="wave" variant="rectangular" width="100%" height={30} />
				</div>
			)}
			<div
				ref={ref} // сюда идёт drag + drop
				className={styles.condition}
				style={{ position: isConditionReady ? "relative" : "absolute", opacity: isConditionReady ? 1 : 0, transition: "opacity 0.5s ease-in" }}
			>
				{showDragIcon && (
					<div onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} className={styles.dragIconWrapper}>
						<FontAwesomeIcon
							onClick={(e) => {
								e.stopPropagation();
							}}
							icon={faGripVertical}
							size="xl"
						/>
					</div>
				)}

				{/* тип условия запуска */}
				<FormControl margin="none" sx={{ minWidth: 24, '.MuiInputLabel-root[data-shrink="false"]': { top: -8 }, ".MuiSelect-select": { paddingBlock: 0 } }}>
					<Hint hint={sesHints.hint_sesConfigureActionConditionTypes} position="left" hintWidth="220px">
						<IconSelect<ConditionType | "yes" | "no">
							disabled={isDisable}
							value={extendedselectConditionType}
							onChange={changeTypeConditionRunHandler}
							iconList={CONDITIONS_LIST_ICONS}
							itemsList={conditionsTypesList}
							mode={"fullIcon"}
							mainIconColor={colorPrimary}
							withBorder={true}
							widthHeightSize={23}
							menuFontmultiplierFromWidth={2}
						/>
					</Hint>
				</FormControl>

				{/* подтверждение */}
				{conditionData.type === "confirmation" && (
					<FormControl margin="none" sx={{ minWidth: 22, mr: "auto", '.MuiInputLabel-root[data-shrink="false"]': { top: -8 }, ".MuiSelect-select": { paddingBlock: 0 } }}>
						<Typography sx={{ fontSize: 10, m: " 0", width: "100%", textAlign: "left", fontStyle: "italic", whiteSpace: "pre-line", maxWidth: "550px", lineHeight: 1.2 }}>
							{translate(confirmationValue === "yes" ? "selectItem_haveUserConfirmation" : "selectItem_haveNothaveUserConfirmation")}
						</Typography>
					</FormControl>
				)}

				{/* глубина поиска */}
				{conditionData.type === "data" && (
					<FormControl margin="none" sx={{ minWidth: 22, '.MuiInputLabel-root[data-shrink="false"]': { top: -8 }, ".MuiSelect-select": { paddingBlock: 0 } }}>
						<Hint hint={sesHints.hint_sesConfigureDataSearchScope} position="top" offset={[0, -8]}>
							<IconSelect<DepthType>
								disabled={isDisable}
								value={selectDepth}
								onChange={handleChangeSelectDepth}
								iconList={DEPTH_OPTIONS_ICONS}
								itemsList={DEPTH_OPTIONS_LIST}
								mode={"fullIcon"}
								mainIconColor={colorPrimary}
								withBorder={false}
								widthHeightSize={22}
								menuFontmultiplierFromWidth={2}
							/>
						</Hint>
					</FormControl>
				)}

				{conditionData.type === "interval" && (
					<>
						{/* не равен */}
						<CustomTypographyAsCheckbox
							label={checkIntervalEquality ? "selectItem_except" : "selectItem_only"}
							checked={checkIntervalEquality}
							onChange={changeCheckIntervalEquality}
							disabled={isDisable}
						/>
						{/* название интервала */}
						<FormControl margin="none" sx={{ flexGrow: 1, minWidth: 200 }}>
							<TextfieldWithAutocompleteSmall
								autocompleteOptions={timeIntervalsOptions}
								value={formik.values.selectedId}
								setValues={formik.setValues}
								values={formik.values}
								onChange={(e) => formik.handleChange(e)}
								name="selectedId"
								isDisabled={isDisable}
								isError={formik.values.selectedId === ""}
							/>
						</FormControl>
					</>
				)}

				{conditionData.type === "data" && (
					<div className={styles.dataFieldWrapper}>
						{/* название ЭД */}
						<FormControl margin="none" sx={{ flexGrow: 0.5, minWidth: "150px" }} fullWidth>
							<TextfieldWithAutocompleteSmall
								autocompleteOptions={dataElemOptions}
								value={formik.values.inputDataElem}
								setValues={formik.setValues}
								values={formik.values}
								onChange={(e) => formik.handleChange(e)}
								name="inputDataElem"
								isDisabled={isDisable}
								isError={formik.values.inputDataElem === ""}
							/>
						</FormControl>
						{/* кнопка информации о недостающем элементе */}
						{(conditionFor.for === "endpointRun" || conditionFor.for === "endpointRunInEntry") &&
							dataElemList.data.find((dataElem) => dataElem.id === conditionData.id && !dataElem.hasNearestAction) && (
								<div className={styles.dataFieldIcon}>
									<IconWithPopupInfo iconSize="lg" iconColor={colorRed}>
										{translate("title_eventNotFilledMissingElement")}
									</IconWithPopupInfo>
								</div>
							)}
					</div>
				)}

				{/* событие */}
				{conditionData.type === "event" && (
					<FormControl margin="none" fullWidth sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 }, ".MuiSelect-select": { paddingBlock: 0 } }}>
						<Hint hint={translate("hint_conditionOperators")} position="top">
							<>
								<Select
									value={selectEventId}
									onChange={(e) => setSelectEventId(e.target.value as RobotActionEventType)}
									onBlur={(e) => conditionData.id !== e.target.value && changeValuesConditionRunHandler()}
									disabled={isDisable}
									fullWidth
									variant={"standard"}
									sx={{
										"& .MuiSelect-select": {
											pl: "4px !important",
											display: "flex",
											alignItems: "center",
											fontSize: "10px !important",
											backgroundColor: "rgba(0, 0, 0, 0)",
										},
										fontSize: 10,
										height: 23,
										color: colorPrimary,
										backgroundColor: "#efefef",
										borderRadius: "4px ",
										"&:before": {
											borderBottom: "none",
										},
										"&:after": {
											borderBottom: "none",
										},
										"&:hover:not(.Mui-disabled):before": {
											borderBottom: "none",
										},
										"& .MuiInputBase-root": {
											backgroundColor: "#efefef !important",
											"&:hover": {
												backgroundColor: "#efefef !important",
											},
											"&.Mui-focused": {
												backgroundColor: "#efefef !important",
											},
											"&.Mui-disabled": {
												backgroundColor: "#efefef !important",
												opacity: 0.6,
											},
										},
										"& .MuiInput-input": {
											backgroundColor: "#efefef !important",
										},
										"&.MuiInput-root": {
											"&:before": {
												borderBottom: "none",
											},
											"&:after": {
												borderBottom: "none",
											},
											backgroundColor: "#efefef !important",
										},
									}}
								>
									{ROBOT_EVENT_LIST.map(({ event, translation }) => {
										// const hintKey = translation.startsWith("selectItem_") ? "hint_" + translation.replace("selectItem_", "") : "hint_" + translation;

										return (
											<CustomMenuItem key={event} value={event} sx={{ fontSize: 10 }} hintPosition="left">
												{translate(translation)}
											</CustomMenuItem>
										);
									})}
								</Select>
							</>
						</Hint>
					</FormControl>
				)}

				{/* переменная */}
				{conditionData.type === "variable" && (
					<FormControl margin="none" fullWidth sx={{ flexGrow: 0.5, minWidth: 150, marginLeft: "2px" }}>
						<TextfieldWithAutocompleteSmall
							freeSolo
							autocompleteOptions={variableNamesOptions}
							value={formik.values.selectedId}
							setValues={formik.setValues}
							values={formik.values}
							onChange={(e) => formik.handleChange(e)}
							name="selectedId"
							isDisabled={isDisable}
							isError={formik.values.selectedId === ""}
						/>
					</FormControl>
				)}

				{(conditionData.type === "data" || conditionData.type === "variable") && (
					<>
						{/* операция */}
						<FormControl margin="none" sx={{ minWidth: 22, '.MuiInputLabel-root[data-shrink="false"]': { top: -8 }, ".MuiSelect-select": { paddingBlock: 0 } }}>
							<Hint hint={sesHints.hint_sesConfigureConditionOperators} position="top" offset={[0, -8]}>
								<IconSelect<OperationType>
									disabled={isDisable}
									value={selectOperation}
									onChange={handleChangeSelectOperation}
									iconList={OPERATION_CONDITIONS_ICONS}
									itemsList={OPERATION_CONDITIONS_LIST}
									mode={"fullIcon"}
									mainIconColor={colorPrimary}
									withBorder={false}
									widthHeightSize={22}
									menuFontmultiplierFromWidth={2}
								/>
							</Hint>
						</FormControl>

						{/* значение */}
						<FormControl
							margin="none"
							fullWidth
							sx={{
								minWidth: selectOperation === "exists" || selectOperation === "not_exists" ? 80 : 150,
								opacity: selectOperation === "exists" || selectOperation === "not_exists" ? 0.4 : undefined,
								maxWidth: "250px",
							}}
						>
							<TextfieldWithAutocompleteSmall
								freeSolo
								autocompleteOptions={conditionData.type === "variable" ? variableValuesOptions : []}
								value={formik.values.inputValue}
								setValues={formik.setValues}
								values={formik.values}
								onChange={(e) => formik.handleChange(e)}
								name="inputValue"
								isDisabled={isDisable || selectOperation === "exists" || selectOperation === "not_exists"}
								isError={formik.values.inputValue === "" && selectOperation !== "exists" && selectOperation !== "not_exists"}
							/>
						</FormControl>
					</>
				)}

				{/* удаление условия */}
				{!isDisable && (
					<ButtonIcon
						icon={faTrashCan}
						iconSize="sm"
						iconColor={colorRed}
						onClick={deleteConditionHandler}
						title={translate("button_deleteCondition")}
						style={{ height: "22px", minWidth: "22px" }}
					/>
				)}
			</div>
		</>
	);
};

export default memo(Condition, (prev: IConditionProps, next: IConditionProps) => {
	// Основные объекты и массивы сравниваем по ссылке (должны быть мемоизированы)
	const isConditionDataSame = prev.conditionData === next.conditionData;
	const isDataElemListOptionsSame = prev.dataElemListOptions === next.dataElemListOptions;

	// Булевы пропсы
	const isFlagsSame =
		prev.isDisable === next.isDisable &&
		prev.isConditionReady === next.isConditionReady &&
		prev.showDragIcon === next.showDragIcon &&
		prev.conditionWithOrOption === next.conditionWithOrOption;

	// Числовые индексы
	const isIndexesSame =
		prev.conditionBlockIdx === next.conditionBlockIdx &&
		prev.conditionIdx === next.conditionIdx &&
		prev.dragIdx === next.dragIdx &&
		prev.orBlockIndex === next.orBlockIndex;

	return isConditionDataSame && isDataElemListOptionsSame && isFlagsSame && isIndexesSame;
});
